import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Container, Form, Modal, Row, Col, Spinner, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { MEMO_CATEGORIES, MEMO_STATUS, UPLOAD_BASE, fmtDateTimeTH } from './aoShared'

const emptyForm = { mm_category: 'บันทึกข้อความ', mm_subject: '', mm_body: '' }

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }, { indent: '-1' }, { indent: '+1' }],
    ['clean'],
  ],
}

// บันทึกข้อความของฉัน — ครูทุกคนเขียนบันทึก/คำขอ เสนอผ่านธุรการถึงผู้บริหาร
export default function MyMemos() {
  const [memos, setMemos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [file, setFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get('/api/ao/my/memos')
      if (res.data.success) setMemos(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const openCreate = () => {
    setEditId(null)
    setForm(emptyForm)
    setFile(null)
    setModalError('')
    setShowModal(true)
  }

  const openEdit = (m) => {
    setEditId(m.mm_id)
    setForm({ mm_category: m.mm_category, mm_subject: m.mm_subject, mm_body: m.mm_body || '' })
    setFile(null)
    setModalError('')
    setShowModal(true)
  }

  // save: 'draft' บันทึกร่าง | 'submit' ส่งถึงธุรการ
  const handleSave = async (mode) => {
    if (!form.mm_subject.trim()) {
      setModalError('กรุณาระบุเรื่อง')
      return
    }
    setSaving(true)
    setModalError('')
    try {
      const fd = new FormData()
      fd.append('mm_category', form.mm_category)
      fd.append('mm_subject', form.mm_subject)
      fd.append('mm_body', form.mm_body || '')
      if (file) fd.append('file', file)
      if (editId) {
        fd.append('resubmit', mode === 'submit' ? 'true' : 'false')
        await axiosInstance.put(`/api/ao/my/memos/${editId}`, fd)
      } else {
        fd.append('mm_status', mode === 'submit' ? 'submitted' : 'draft')
        await axiosInstance.post('/api/ao/my/memos', fd)
      }
      setShowModal(false)
      if (mode === 'submit') {
        Swal.fire({ icon: 'success', title: 'ส่งถึงธุรการเรียบร้อย', text: 'ธุรการจะตรวจและเสนอผู้บริหารต่อไป', timer: 2200, showConfirmButton: false })
      }
      fetchData()
    } catch (err) {
      setModalError(err.response?.data?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const handleSubmitDraft = async (m) => {
    const result = await Swal.fire({
      icon: 'question',
      title: 'ส่งบันทึกถึงธุรการ?',
      text: m.mm_subject,
      showCancelButton: true,
      confirmButtonText: 'ส่ง',
      cancelButtonText: 'ปิด',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.put(`/api/ao/my/memos/${m.mm_id}/submit`)
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ส่งไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const handleDelete = async (m) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'ลบฉบับร่างนี้?',
      text: m.mm_subject,
      showCancelButton: true,
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#dc3545',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.delete(`/api/ao/my/memos/${m.mm_id}`)
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ลบไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-draft-line me-2 text-primary" />
              บันทึกข้อความของฉัน
            </h4>
            <Button variant="primary" size="sm" onClick={openCreate}>
              <i className="ri-add-line me-1" />เขียนบันทึก
            </Button>
          </div>

          <Alert variant="light" className="border fs-sm py-2">
            <i className="ri-information-line me-1" />
            บันทึกข้อความ/คำขออนุญาตจะส่งถึงงานธุรการเพื่อออกเลขและเสนอแฟ้มผู้บริหารพิจารณา
            — ผลการพิจารณาจะแจ้งกลับผ่านการแจ้งเตือน
          </Alert>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <Card className="card-one">
              <Card.Body className="p-0">
                {memos.length === 0 ? (
                  <div className="text-center text-muted py-4">ยังไม่มีบันทึกข้อความ</div>
                ) : (
                  <Table responsive hover className="mb-0 fs-sm">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 90 }}>เลขบันทึก</th>
                        <th style={{ width: 170 }}>ประเภท</th>
                        <th>เรื่อง</th>
                        <th style={{ width: 140 }}>วันที่</th>
                        <th style={{ width: 130 }}>สถานะ</th>
                        <th style={{ width: 150 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {memos.map(m => {
                        const st = MEMO_STATUS[m.mm_status] || MEMO_STATUS.draft
                        return (
                          <tr key={m.mm_id}>
                            <td>{m.mm_full_no || '-'}</td>
                            <td>{m.mm_category}</td>
                            <td>
                              <span className="fw-semibold">{m.mm_subject}</span>
                              {m.mm_file && (
                                <a className="ms-2" href={`${UPLOAD_BASE}/uploads/ao_docs/${m.mm_file}`} target="_blank" rel="noreferrer">
                                  <i className="ri-attachment-2" />
                                </a>
                              )}
                              {m.mm_exec_comment && (
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                  ความเห็นผู้บริหาร: {m.mm_exec_comment}
                                </div>
                              )}
                            </td>
                            <td>{fmtDateTimeTH(m.created_at)}</td>
                            <td><Badge bg={st.bg}>{st.label}</Badge></td>
                            <td className="text-end">
                              <Button as={Link} to={`/my/memos/${m.mm_id}/print`} size="sm" variant="outline-secondary" className="me-1" title="พิมพ์">
                                <i className="ri-printer-line" />
                              </Button>
                              {['draft', 'returned'].includes(m.mm_status) && (
                                <>
                                  <Button size="sm" variant="outline-primary" className="me-1" title="แก้ไข" onClick={() => openEdit(m)}>
                                    <i className="ri-edit-line" />
                                  </Button>
                                  <Button size="sm" variant="outline-success" className="me-1" title="ส่งถึงธุรการ" onClick={() => handleSubmitDraft(m)}>
                                    <i className="ri-send-plane-line" />
                                  </Button>
                                </>
                              )}
                              {m.mm_status === 'draft' && (
                                <Button size="sm" variant="outline-danger" title="ลบ" onClick={() => handleDelete(m)}>
                                  <i className="ri-delete-bin-line" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          )}
        </Container>
      </div>
      <Footer />

      {/* Create/edit modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">{editId ? 'แก้ไขบันทึกข้อความ' : 'เขียนบันทึกข้อความ'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}
          <Row className="g-2 mb-2">
            <Col xs={5}>
              <Form.Label className="fs-sm mb-1">ประเภทเรื่อง</Form.Label>
              <Form.Select size="sm" value={form.mm_category}
                onChange={e => setForm(f => ({ ...f, mm_category: e.target.value }))}>
                {MEMO_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </Form.Select>
            </Col>
            <Col xs={7}>
              <Form.Label className="fs-sm mb-1">เรื่อง <span className="text-danger">*</span></Form.Label>
              <Form.Control size="sm" type="text" maxLength={500} required value={form.mm_subject}
                onChange={e => setForm(f => ({ ...f, mm_subject: e.target.value }))} />
            </Col>
          </Row>
          <Form.Group className="mb-2">
            <Form.Label className="fs-sm mb-1">เนื้อหา</Form.Label>
            <ReactQuill theme="snow" modules={QUILL_MODULES} value={form.mm_body}
              onChange={v => setForm(f => ({ ...f, mm_body: v }))} style={{ background: '#fff' }} />
          </Form.Group>
          <Form.Group>
            <Form.Label className="fs-sm mb-1">ไฟล์แนบ <span className="text-muted">— ไม่บังคับ</span></Form.Label>
            <Form.Control size="sm" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
              onChange={e => setFile(e.target.files[0] || null)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>ปิด</Button>
          <Button variant="outline-primary" size="sm" disabled={saving} onClick={() => handleSave('draft')}>
            <i className="ri-save-line me-1" />บันทึกร่าง
          </Button>
          <Button variant="primary" size="sm" disabled={saving} onClick={() => handleSave('submit')}>
            {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-send-plane-line me-1" />}
            ส่งถึงธุรการ
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
